import { FC, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";

import { Panel } from "capitalrx-components-2.0/lib/general";
import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";

import { AccumulatorProgressBar } from "@/App/components/AccumulatorProgressBar";
import { MedicalCareUsageAccumulationValues } from "@/App/types/medicalServiceMaximums";

const StyledPanel = styled(Panel)`
  background-color: #ffffff !important;
  border-color: #cfd9e1 !important;
  border-radius: 8px !important;
  border: 1px solid #cfd9e1 !important;
  height: 100%;
  padding: 24px !important;
`;

const StyledSemibold = styled(Semibold)`
  color: #60646D !important;
  min-height: 1.5rem;
`;

export interface MedicalCareUsageAccumulatorCardProps {
  readonly title: string;
  readonly inNetwork?: boolean;
  readonly outOfNetwork?: boolean;
  readonly accumulator: MedicalCareUsageAccumulationValues;
};

const TRANSLATION_PATH = "MEDICAL_CARE_USAGE_LIMITS.medicalCareUsageAccumulatorCard";

export const MedicalCareUsageAccumulatorCard: FC<MedicalCareUsageAccumulatorCardProps> = ({ title, inNetwork, outOfNetwork, accumulator }) => {
  const { t: translate } = useTranslation();

  const networks = useMemo(() => {
    if (inNetwork && outOfNetwork || !inNetwork && !outOfNetwork) {
      return translate(`${TRANSLATION_PATH}.bothNetworks`);
    }
    if (inNetwork) {
      return translate(`${TRANSLATION_PATH}.inNetworkOnly`);
    }
    if (outOfNetwork) {
      return translate(`${TRANSLATION_PATH}.outOfNetworkOnly`);
    }
  }, [inNetwork, outOfNetwork]);

  const limitReached = useMemo(() => {
    return accumulator.remaining.lessThanOrEqualTo(0);
  }, [accumulator.remaining]);

  return (
    <StyledPanel>
      <Stack spacing="medium" style={{ height: "100%" }}>
        <Stack spacing="mini" style={{ height: "100%", justifyContent: "flex-start" }}>
          <Subheading>{title}</Subheading>
          <StyledSemibold>{networks}</StyledSemibold>
        </Stack>
        <Body>
          {limitReached ? (
            <Trans
              i18nKey={`${TRANSLATION_PATH}.limitReached`}
              values={{
                units: translate(`${TRANSLATION_PATH}.unit_${accumulator.unit}`, { count: 1 }),
              }}
            />
          ) : (
            <Trans
              i18nKey={`${TRANSLATION_PATH}.noLimitReached`}
              values={{
                max: accumulator.max,
                units: translate(`${TRANSLATION_PATH}.unit_${accumulator.unit}`, { count: accumulator.max.toNumber() }),
              }}
            />
          )}
        </Body>
        <AccumulatorProgressBar
          accumulator={accumulator}
          spentHelpText={translate(`${TRANSLATION_PATH}.used`)}
          remainingHelpText={translate(`${TRANSLATION_PATH}.remaining`)}
          limitReached={limitReached}
          limitReachedWarning={true}
          iconName="check-mark"
          iconSize="small"
        />
      </Stack>
    </StyledPanel>
  );
};
